import React from "react";
import { useParams } from "react-router-dom";
import { fetchPartners } from "../../../services/partner.service.ts";
import { useAppSettings } from "../../../hooks/useAppSettings.tsx";
import Layout from "../../Layout.tsx";
import Title from "../../Title.tsx";
import TableColaboradores from "./componentes/TableColaboradores.tsx";
import { useDispatch, useSelector } from "react-redux";
import { setColaboradores } from "../../../store/slices/colaboradoresBranchSlice.ts";
import { MenuSelectedColaboradoresEnum } from "../../../types/ColaboradoresSliceType.ts";
import { RootState } from "../../../store/index.ts";
import {
  resetState,
  setLoading,
  setMenu,
  setTotal,
} from "../../../store/slices/colaboradoresSlice.ts";
import { notification } from "../../../utils/Notifications.tsx";
import FiltrosColaboradores from "../../colaboradores/FiltrosColaboradores.tsx";
import PaginationColaboradores from "../../../pages/colaboradores/PaginationColaboradores.tsx";
import BranchSelector from "./componentes/BranchSelector.tsx";
import { DatePicker, ConfigProvider } from "antd";
import esES from "antd/es/locale/es_ES";
import dayjs from "dayjs";
import "dayjs/locale/es";

const { RangePicker } = DatePicker;

const PartnersByBranch = () => {
  useAppSettings();
  let id;

  const { user } = useSelector((state: RootState) => state.auth);
  const { id: ipParams } = useParams();

  if (user.rol === "Lider de sucursal") {
    id = user.branchId;
  } else {
    id = ipParams;
  }

  const dispatch = useDispatch();

  const {
    menuSelectedByBranch,
    page,
    limit,
    param,
    loading,
    fetch,
    query,
    sortBy,
    defaultSort,
  } = useSelector((state: RootState) => state.colaboradores);

  const [dateRange, setDateRange] = React.useState<any>(null);
  const [status, setStatus] =
    React.useState<MenuSelectedColaboradoresEnum>(menuSelectedByBranch);

  React.useEffect(() => {
    setStatus(menuSelectedByBranch);
  }, [menuSelectedByBranch]);

  const fetchColaboradores = async () => {
    dispatch(setLoading(true));
    try {
      const offset = page === 0 ? 0 : page * limit;
      let queryToSend = query;

      // Manejar el estado del tab (activo/inactivo)
      if (!query.includes("tab=")) {
        queryToSend = `${query ? query + "&" : ""}tab=${status}`;
      }

      // Manejar el rango de fechas si existe
      if (dateRange && dateRange[0] && dateRange[1]) {
        const start = dateRange[0].format("YYYY-MM-DD");
        const end = dateRange[1].format("YYYY-MM-DD");
        queryToSend += `&startDate=${start}&endDate=${end}`;
      }

      let filter = `?sort=${sortBy}&order=${defaultSort}&offset=${offset}&limit=${limit}&branch=${id}&${queryToSend}`;
      const response = await fetchPartners(filter);

      dispatch(setColaboradores(response.results));
      dispatch(setTotal(response.total));
    } catch (error) {
      notification("Error", "Error al cargar los colaboradores", "danger");
    } finally {
      dispatch(setLoading(false));
    }
  };

  React.useEffect(() => {
    fetchColaboradores();
  }, [
    page,
    limit,
    menuSelectedByBranch,
    param,
    fetch,
    query,
    sortBy,
    defaultSort,
    id,
    status,
    dateRange,
  ]);

  React.useEffect(() => {
    dispatch(resetState());
  }, []);

  return (
    <Layout loading={loading}>
      <Title
        baseTitle="Sucursales"
        basePath="/sucursales"
        activeTitle="Colaboradores"
        title="Colaboradores"
      />
      <div className="card border-0 m-4 rounded overflow-hidden rounded-3 shadow-sm">
        <div
          className="text-white p-3 d-flex align-items-center"
          style={{
            background: "linear-gradient(245deg, #495057 0%, #212529 100%)",
          }}
        >
          <i className="fa-solid fa-magnifying-glass me-2 fs-14px"></i>
          <h5
            className="mb-0"
            style={{
              fontSize: "12px",
              fontWeight: "600",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}
          >
            Panel de Búsqueda
          </h5>
        </div>
        <div className="p-4 bg-white">
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
            <div style={{ minWidth: "520px" }}>
              <label className="form-label fw-bold text-muted mb-1">
                Sucursal
              </label>
              <BranchSelector currentId={id} variant="simple" />
            </div>

            <div className="d-flex flex-column align-items-center">
              <label className="form-label fw-bold text-muted mb-1">
                Estado
              </label>
              <div
                className="d-flex bg-light p-1 rounded-3 border"
                style={{ height: "40px" }}
              >
                <button
                  className={`btn btn-sm px-4 rounded-3 fw-bold transition-all border-0 d-flex align-items-center ${
                    status === MenuSelectedColaboradoresEnum.activos
                      ? "btn-info text-white shadow-sm"
                      : "text-muted"
                  }`}
                  style={{ fontSize: "11px" }}
                  onClick={() => {
                    setStatus(MenuSelectedColaboradoresEnum.activos);
                    dispatch(setMenu(MenuSelectedColaboradoresEnum.activos));
                  }}
                >
                  <i className="fa-solid fa-user-check me-2"></i> ACTIVOS
                </button>
                <button
                  className={`btn btn-sm px-4 rounded-3 fw-bold transition-all border-0 d-flex align-items-center ${
                    status === MenuSelectedColaboradoresEnum.bajas
                      ? "btn-danger text-white shadow-sm"
                      : "text-muted"
                  }`}
                  style={{ fontSize: "11px" }}
                  onClick={() => {
                    setStatus(MenuSelectedColaboradoresEnum.bajas);
                    dispatch(setMenu(MenuSelectedColaboradoresEnum.bajas));
                  }}
                >
                  <i className="fa-solid fa-user-slash me-2"></i> INACTIVOS
                </button>
              </div>
            </div>

            {/* Date Range - Derecha (End) */}
            <div
              className="d-flex flex-column align-items-end"
              style={{ minWidth: "350px" }}
            >
              <label className="form-label fw-bold text-muted mb-1">
                Fecha
              </label>
              <ConfigProvider locale={esES}>
                <RangePicker
                  className="w-100 shadow-none border"
                  style={{ height: "40px", borderRadius: "8px" }}
                  onChange={(dates) => setDateRange(dates)}
                  placeholder={["Fecha Inicial", "Fecha Final"]}
                  format="DD-MM-YYYY"
                  separator="al"
                />
              </ConfigProvider>
            </div>
          </div>
        </div>
      </div>
      <div className="card border-0 m-4">
        <div className="tab-content p-3">
          <FiltrosColaboradores branch />
          <TableColaboradores />
          <PaginationColaboradores />
        </div>
      </div>
    </Layout>
  );
};

export default PartnersByBranch;
